import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Calendar, CheckCircle, XCircle, RefreshCw, Clock,
  Star, AlertCircle, CreditCard, Mail,
} from 'lucide-react';
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../services/notificationService';
import { formatTimeAgo } from '../../utils/timeAgo';
import './NotificationBell.css';

export const notificationConfig = {
  'interview-booked':      { Icon: Calendar,    color: '#3b82f6', bg: '#eff6ff' },
  'interview-accepted':    { Icon: CheckCircle, color: '#10b981', bg: '#ecfdf5' },
  'interview-declined':    { Icon: XCircle,     color: '#ef4444', bg: '#fef2f2' },
  'interview-rescheduled': { Icon: RefreshCw,   color: '#f97316', bg: '#fff7ed' },
  'interview-reminder':    { Icon: Clock,       color: '#eab308', bg: '#fefce8' },
  'feedback-received':     { Icon: Star,        color: '#a855f7', bg: '#faf5ff' },
  'feedback-reminder':     { Icon: AlertCircle, color: '#f97316', bg: '#fff7ed' },
  'subscription':          { Icon: CreditCard,  color: '#6366f1', bg: '#eef2ff' },
  'newsletter':            { Icon: Mail,        color: '#6b7280', bg: '#f9fafb' },
  'system':                { Icon: Bell,        color: '#6b7280', bg: '#f9fafb' },
};

const getConfig = (type) => notificationConfig[type] || notificationConfig.system;

const NotificationBell = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const data = await getUnreadCount();
      setCount(data?.count ?? 0);
    } catch {}
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getNotifications();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => clearInterval(id);
  }, [fetchCount]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const togglePanel = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchList();
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems((arr) => arr.map((n) => ({ ...n, isRead: true })));
      setCount(0);
    } catch {}
  };

  const handleClickItem = async (n) => {
    try {
      if (!n.isRead) {
        await markNotificationRead(n.id);
        setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
        setCount((c) => Math.max(0, c - 1));
      }
    } catch {}
    if (n.actionUrl) {
      setOpen(false);
      navigate(n.actionUrl);
    }
  };

  const display = count > 99 ? '99+' : count;

  return (
    <div className="nb-wrap" ref={wrapRef}>
      <button
        type="button"
        className="nb-btn"
        aria-label="Notifications"
        onClick={togglePanel}
      >
        <Bell size={20} />
        {count > 0 && <span className="nb-badge">{display}</span>}
      </button>

      {open && (
        <div className="nb-panel">
          <div className="nb-head">
            <span className="nb-title">Notifications</span>
            <button type="button" className="nb-mark" onClick={handleMarkAll}>
              Mark all read
            </button>
          </div>

          <div className="nb-list">
            {loading && <div className="nb-loading">Loading…</div>}

            {!loading && items.length === 0 && (
              <div className="nb-empty">
                <Bell size={32} />
                <p>You're all caught up!</p>
              </div>
            )}

            {!loading && items.map((n) => {
              const cfg = getConfig(n.type);
              const I = cfg.Icon;
              return (
                <div
                  key={n.id}
                  className={`nb-item ${n.isRead ? '' : 'nb-item-unread'}`}
                  onClick={() => handleClickItem(n)}
                >
                  <div className="nb-ic" style={{ background: cfg.bg, color: cfg.color }}>
                    <I size={16} />
                  </div>
                  <div className="nb-body">
                    <div className="nb-row">
                      <div className="nb-title-text">{n.title}</div>
                      <div className="nb-time">{formatTimeAgo(n.createdAt)}</div>
                    </div>
                    <div className="nb-msg">{n.message}</div>
                  </div>
                  {!n.isRead && <span className="nb-dot" />}
                </div>
              );
            })}
          </div>

          {!loading && items.length > 0 && (
            <div className="nb-foot">
              <button
                type="button"
                className="nb-view-all"
                onClick={() => { setOpen(false); navigate('/notifications'); }}
              >
                View all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
